import { supabase } from './supabase';
import type { Database } from './database.types';

export type Message = Database['public']['Tables']['messages']['Row'];
export type ChatRoom = Database['public']['Tables']['chat_rooms']['Row'];
export type ChatParticipant = Database['public']['Tables']['chat_participants']['Row'];

export const createChatRoom = async (participantIds: string[]) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('Not authenticated');

    // For 1:1 chats, ensure we have exactly two participants
    if (participantIds.length !== 1) {
      throw new Error('Currently only 1:1 chats are supported');
    }

    const otherUserId = participantIds[0];

    // Check if a chat room already exists between these users
    const { data: existingRoom, error: roomError } = await supabase
      .from('chat_rooms')
      .select('id')
      .or(`and(participant1_id.eq.${user.id},participant2_id.eq.${otherUserId}),and(participant1_id.eq.${otherUserId},participant2_id.eq.${user.id})`)
      .maybeSingle();

    if (roomError) throw roomError;
    if (existingRoom) return existingRoom.id;

    // Create new chat room with participants
    const { data: room, error: createError } = await supabase
      .from('chat_rooms')
      .insert({
        participant1_id: user.id,
        participant2_id: otherUserId
      })
      .select()
      .single();

    if (createError) throw createError;

    // Add participants
    const participants = [
      { chat_room_id: room.id, profile_id: user.id },
      { chat_room_id: room.id, profile_id: otherUserId }
    ];

    const { error: participantsError } = await supabase
      .from('chat_participants')
      .insert(participants);

    if (participantsError) throw participantsError;

    return room.id;
  } catch (err) {
    console.error('Error creating chat room:', err);
    throw err;
  }
};

export const sendMessage = async (chatRoomId: string, content: string) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('messages')
      .insert({
        chat_room_id: chatRoomId,
        sender_id: user.id,
        content
      })
      .select(`
        *,
        sender:profiles!inner(
          id,
          full_name,
          profile_picture
        )
      `)
      .single();

    if (error) throw error;
    return data;
  } catch (err) {
    console.error('Error sending message:', err);
    throw err;
  }
};

export const getChatRooms = async () => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('Not authenticated');

    const { data: rooms, error: roomsError } = await supabase
      .from('chat_rooms')
      .select(`
        *,
        participant1:profiles!chat_rooms_participant1_id_fkey(
          id,
          full_name,
          profile_picture,
          location
        ),
        participant2:profiles!chat_rooms_participant2_id_fkey(
          id,
          full_name,
          profile_picture,
          location
        ),
        latest_message:messages(
          id,
          content,
          created_at,
          sender:profiles!inner(
            id,
            full_name
          )
        )
      `)
      .or(`participant1_id.eq.${user.id},participant2_id.eq.${user.id}`)
      .order('updated_at', { ascending: false });

    if (roomsError) throw roomsError;

    // Transform the data to match the expected format
    const transformedRooms = (rooms || []).map(room => {
      const otherParticipant = room.participant1_id === user.id ? room.participant2 : room.participant1;
      return {
        ...room,
        participants: [otherParticipant]
      };
    });

    return transformedRooms;
  } catch (err) {
    console.error('Error fetching chat rooms:', err);
    return [];
  }
};

export const getMessages = async (chatRoomId: string) => {
  try {
    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        sender:profiles!inner(
          id,
          full_name,
          profile_picture
        )
      `)
      .eq('chat_room_id', chatRoomId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  } catch (err) {
    console.error('Error fetching messages:', err);
    throw err;
  }
};

export const subscribeToMessages = (chatRoomId: string, callback: (message: Message) => void) => {
  return supabase
    .channel(`chat:${chatRoomId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `chat_room_id=eq.${chatRoomId}`
      },
      async (payload) => {
        // Fetch complete message with sender info
        const { data: message, error } = await supabase
          .from('messages')
          .select(`
            *,
            sender:profiles!inner(
              id,
              full_name,
              profile_picture
            )
          `)
          .eq('id', payload.new.id)
          .single();

        if (error) {
          console.error('Error fetching new message:', error);
          return;
        }

        callback(message);
      }
    )
    .subscribe();
};